import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0517 from "../lesson-17/lesson";

const linkLesson0517 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0517.id);

const theory = ` <h3 class=sectionHead>What you will learn in this lesson?</h3>
<p>In this lesson you will learn that the sound insulation between two rooms depends both on the performance of the wall separating them and on the transmission of vibrations via the side walls, floor and ceiling (flanking transmission). You will see the different elements that are taken into account in the normative calculation of the apparent sound reduction index of a partition wall, so that you can understand how to improve the sound insulation by treating the propagation of vibrations via the side walls, floor and ceiling.<br class=newline>
<h3 class=sectionHead>Introduction</h3>
<p>The weighted sound reduction index of a building element was described in <a href="${linkLesson0517}" target="_blank">Lesson 5.17</a>. It is an essential parameter in the evaluation of sound insulation between spaces and depends on the nature of the material used and the construction of the building element. In this lesson, the contributions of the side walls, floor and ceiling due to the transmission of vibrations (“flanking”) in them are added to the basic sound reduction properties of a wall as an example of a building element. A new apparent sound reduction index of that wall is then calculated, and it depends on the direct sound transmission through the separating wall and on the lateral flanking transmissions.<br class=newline>
<p>The radiated sound from a structural element can be considered as the sum of the sound transmission of structure-borne sound through various paths.<br class=newline>
<p>Theoretical aspects are explained in the first chapters, then different solutions for vibration isolation are presented. Finally, a calculation example is given at the end of the lesson.<br class=newline>
<h3 class=sectionHead>Definition of the different transmission paths and calculation of the apparent sound reduction index</h3>
<p>
   As described in the introduction, we are interested in the insulation between two spaces separated by a wall. Sound propagates directly through the separating wall, but also by flanking through rigidly connected structural elements. <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-1.svg data-img-alt="Figure 1. Definition of sound transmission paths between two rooms.">Figure 1</a> shows the different paths considered in the normative calculation.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-1.svg alt="Figure 1. Definition of sound transmission paths between two rooms."><figcaption class=caption>Figure 1. Definition of sound transmission paths between two rooms.</figcaption></figure>
<p>In this image, for the sake of clarity, only the flanking path via the ceiling is shown, but the same paths exist via the walls and the floor.<br class=newline>
<p>So we have:<br class=newline>
<ul class=itemize1>
   <li class=itemize>
      the Direct-direct (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Dd</mi>
      </math>
      ) path;
   <li class=itemize>
      the paths that start at the partition wall and propagate through the side walls (or floor, or ceiling) of the reception space: Direct-flanking (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Df</mi>
      </math>
      );
   <li class=itemize>
      paths that pass through the side walls (or floor, or ceiling) of the first space and propagate through the separating wall: Flanking-direct (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Fd</mi>
      </math>
      );
   <li class=itemize>
      paths that start in the side walls in the transmitting space and propagate into the side walls of the receiving space: Flanking-flanking (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Ff</mi>
      </math>
      ).
</ul>
<p>
   According to the international standard ISO 12354-1 [1], the apparent sound reduction index (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mi>w</mi>

   </math>
   ) of the wall is equal to the logarithmic sum of the sound reduction indices of all the propagation paths, and can be calculated as follows:<br class=newline>
   <table class=equation>
   <tr>
      <td style="font-size: 4px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msup>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">′</mi>
               </mrow>
            </msup>
            <mi>w</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mo class=MathClass-bin>∗</mo>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <msup>
               <mrow>
                  <mn>10</mn>
               </mrow>
               <mrow>
                  <mfrac scriptlevel="0">
                     <mrow>
                        <mo scriptlevel="0">−</mo>
                        <msub>
                           <mrow>
                              <mi scriptlevel="0">R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic scriptlevel="0">Dd</mi>
                              <mo scriptlevel="0">,</mo>
                              <mi scriptlevel="0">w</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn scriptlevel="0">10</mn>
                     </mrow>
                  </mfrac>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">F</mi>
                  <mo class=MathClass-rel scriptlevel="0">=</mo>
                  <mi scriptlevel="0">f</mi>
                  <mo class=MathClass-rel scriptlevel="0">=</mo>
                  <mn scriptlevel="0">1</mn>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">n</mi>
               </mrow>
            </munderover>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mfrac scriptlevel="0">
                     <mrow>
                        <mo class=MathClass-bin scriptlevel="0">−</mo>
                        <msub>
                           <mrow>
                              <mi scriptlevel="0">R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic scriptlevel="0">Ff</mi>
                              <mo class=MathClass-punc scriptlevel="0">,</mo>
                              <mi scriptlevel="0">w</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn scriptlevel="0">10</mn>
                     </mrow>
                  </mfrac>
               </mrow>
            </msup>
            <mo class=MathClass-bin scriptlevel="0">+</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">f</mi>
                  <mo class=MathClass-rel scriptlevel="0">=</mo>
                  <mn scriptlevel="0">1</mn>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">n</mi>
               </mrow>
            </munderover>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mfrac scriptlevel="0">
                     <mrow>
                        <mo class=MathClass-bin scriptlevel="0">−</mo>
                        <msub>
                           <mrow>
                              <mi scriptlevel="0">R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic scriptlevel="0">Df</mi>
                              <mo class=MathClass-punc scriptlevel="0">,</mo>
                              <mi scriptlevel="0">w</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn scriptlevel="0">10</mn>
                     </mrow>
                  </mfrac>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <munderover accent=false accentunder=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">F</mi>
                  <mo class=MathClass-rel scriptlevel="0">=</mo>
                  <mn scriptlevel="0">1</mn>
               </mrow>
               <mrow>
                  <mi scriptlevel="0">n</mi>
               </mrow>
            </munderover>
            <mn>1</mn>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mfrac scriptlevel="0">
                     <mrow>
                        <mo class=MathClass-bin scriptlevel="0">−</mo>
                        <msub>
                           <mrow>
                              <mi scriptlevel="0">R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic scriptlevel="0">Fd</mi>
                              <mo class=MathClass-punc scriptlevel="0">,</mo>
                              <mi scriptlevel="0">w</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn scriptlevel="0">10</mn>
                     </mrow>
                  </mfrac>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>With :<br class=newline>
<ul class=itemize1>
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>RDd</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>w</mi>
      </math>
      weighted sound reduction index for direct transmission,
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>RFf</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>w</mi>
      </math>
      (respectively
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>RDf</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>w</mi>
      </math>
      and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>RFd</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>w</mi>
      </math>
      ) weighted lateral sound reduction index for the transmission path
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Ff</mi>
      </math>
      (respectively
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Df</mi>
      </math>
      and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Fd</mi>
      </math>
      ),
   <li class=itemize>n, the number of lateral elements, generally equal to 4 (2 side walls, the floor and the ceiling).
</ul>
<p>Each weighted sound reduction index for each path can be determined as a function of several parameters and is the subject of the next chapter of this lesson.<br class=newline>
<p>
   The main point of this equation is that the calculated index will always be lower than the smallest of the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   indices. Thus, if one seeks to improve the acoustic insulation between two rooms, all the paths must be considered and evaluated in order to obtain the global acoustic insulation: any acoustic leakage or any acoustically weaker path greatly reduces the global insulation.
<h3 class=sectionHead>Determination of the sound reduction index of a path</h3>
<p>
   The sound reduction index
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>RDd</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   corresponds to the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   index of the wall, determined by the characteristics of the material, the mass law, a laboratory test report or other.<br class=newline>
<p>
   For the other indices,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>RDf</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>RFd</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>RFf</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   , they can be calculated using the following formula:<br class=newline>
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ij</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ij</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ij</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mo class=MathClass-bin>∗</mo>
            <mi mathvariant=italic>log</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>With :<br class=newline>
<ul class=itemize1>
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Ri</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>w</mi>
      </math>
      (respectively
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Rj</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>w</mi>
      </math>
      ) is the weighted sound reduction index of element i (respectively j),
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>Δ</mi>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ij</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </mrow>
         </msub>
      </math>
      is the improvement of the sound reduction index by adding a lining on element i and/or element j,
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Kij</mi>
      </math>
      is the vibration loss index for the transmission path ij,
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Ss</mi>
      </math>
      is the area of the separative surface,
   <li class=itemize>
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>lf</mi>
      </math>
      is the current coupling length of the joint between the partition wall and the side walls F and f.
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>
      is the reference length of 1m.
</ul>
<p>
   The sound reduction values
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Ri</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   are determined from the material characteristics (mass law, laboratory test report, etc.).<br class=newline>
<p>
   The lining improvement term
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Δ</mi>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is described in the next chapter of this lesson.<br class=newline>
<p>
   The value of the vibration loss index
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Kij</mi>
   </math>
   can be obtained from charts and depends on the type of joint between the two elements i and j, and the ratio of the surface masses between these elements. For example, the following graph taken from ISO 12354-1 [1] gives the value of the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Kij</mi>
   </math>
   term according to the ratio of masses between two elements.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-2.svg alt="Figure 2. Determination of the <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Kij</mi></math> term for a rigid cross-junction as a function of the surface mass of the building elements."><figcaption class=caption>Figure 2. Determination of the <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Kij</mi></math> term for a rigid cross-junction as a function of the surface mass of the building elements.</figcaption></figure>
<p>
   Thus, for a rigid cross-junction of two perpendicular elements of similar mass,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Kij</mi>
   </math>
   is about 10 dB. This is the case in the example given in chapter 7.<br class=newline>
<p>
   As a conclusion to the theoretical aspects of this lesson, if a very good degree of isolation is required between two spaces (for example two adjoining concert halls or theatres), it is recommended to add vibration isolated linings to the entire walls, floor and ceiling. This creates a structurally independent and vibration isolated inner box. This construction system is known as the ”box-in-box” principle.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-3.svg alt="Figure 3. Schematic diagram of the box-in-box concept, where all surfaces of a space, i.e. all walls, the floor and the ceiling, are vibration isolated from the surrounding structure. This is typically applied in recording studios, music practice rooms, cinemas and other spaces where a high degree of sound isolation is required because of the high sound levels involved. Image not to scale."><figcaption class=caption>Figure 3. Schematic diagram of the box-in-box concept, where all surfaces of a space, i.e. all walls, the floor and the ceiling, are vibration isolated from the surrounding structure. This is typically applied in recording studios, music practice rooms, cinemas and other spaces where a high degree of sound isolation is required because of the high sound levels involved. Image not to scale.</figcaption></figure>
<h3 class=sectionHead>Improvement of the sound attenuation index by lining</h3>
<p>
   The lining improvement term
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Δ</mi>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   for the i to j path can be determined according to the lining of either i or j or both by the following formula:<br class=newline>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ij</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>Δ</mi>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>or
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ij</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Δ</mi>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   With :<br class=newline>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRi</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   (respectively
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRj</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   ) the improvement of the sound reduction index of element i (respectively j). The lowest
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Δ</mi>
   </math>
   R value (between
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRi</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRj</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>w</mi>
   </math>
   ) is to be divided by 2.<br class=newline>
<p>
   The different values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Δ</mi>
   </math>
   R cannot be negative in the formula, they must be set to 0 if they would indeed be negative.
<h3 class=sectionHead>Examples of linings to improve the weighted sound reduction index</h3>
<p>
   New
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   values by adding a lining to improve an existing situation are determined either by calculation methods or by laboratory test reports. Some examples of vibration isolation solutions are given in this chapter.<br class=newline>
<h4 class=subsectionHead>Wall lining</h4>
<p>
   <strong><span class=cmbx-10>0.</span></strong> Single wall of 14 cm thick concrete:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 53 dB<br class=newline><br class=newline><strong><span class=cmbx-10>1.</span></strong> New structurally independent masonry wall, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 81 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +28 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-4.svg data-img-alt="Figure 4. Two structurally independent masonry walls thanks to the following two elements: a flexible material such as mineral wool separates the two walls; one of the walls rests on a neoprene layer (image source: Isolgomma).">Figure 4</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-4.svg alt="Figure 4. Two structurally independent masonry walls thanks to the following two elements: a flexible material such as mineral wool separates the two walls; one of the walls rests on a neoprene layer (image source: Isolgomma)."><figcaption class=caption>Figure 4. Two structurally independent masonry walls thanks to the following two elements: a flexible material such as mineral wool separates the two walls; one of the walls rests on a neoprene layer (image source: Isolgomma).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>2.</span></strong> Plasterboard lining on metal frame rigidly connected to the concrete wall, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 60 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +7 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-5.svg data-img-alt="Figure 5. A plasterboard lining on a substructure rigidly connected to the concrete wall (image source: Isolgomma).">Figure 5</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-5.svg alt="Figure 5. A plasterboard lining on a substructure rigidly connected to the concrete wall (image source: Isolgomma)."><figcaption class=caption>Figure 5. A plasterboard lining on a substructure rigidly connected to the concrete wall (image source: Isolgomma).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>3.</span></strong> Plasterboard lining on substructure with vibration isolating elements, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 71 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +18 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-6.svg data-img-alt="Figure 6. Lining of a masonry wall with plasterboard on a framework by means of vibration isolating fixings (image source: Isover).">Figure 6</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-6.svg alt="Figure 6. Lining of a masonry wall with plasterboard on a framework by means of vibration isolating fixings (image source: Isover)."><figcaption class=caption>Figure 6. Lining of a masonry wall with plasterboard on a framework by means of vibration isolating fixings (image source: Isover).</figcaption></figure>
<h4 class=subsectionHead>Floor lining</h4>
<p>
   <strong><span class=cmbx-10>0.</span></strong> 20 cm thick continuous concrete slab:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 59 dB<br class=newline><strong><span class=cmbx-10>1.</span></strong> New floating wood floor, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 66 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +7 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-7.svg data-img-alt="Figure 7. New wooden floor on neoprene strips with mineral wool mat in the cavity under the floor (image source: CDM).">Figure 7</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-7.svg alt="Figure 7. New wooden floor on neoprene strips with mineral wool mat in the cavity under the floor (image source: CDM)."><figcaption class=caption>Figure 7. New wooden floor on neoprene strips with mineral wool mat in the cavity under the floor (image source: CDM).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>2.</span></strong> Floating slab (14 cm) on neoprene pads or strips, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 81 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +22 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-8.svg data-img-alt="Figure 8. Floating concrete slab on neoprene strips with mineral wool mat in the void under the slab (image source: CDM).">Figure 8</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-8.svg alt="Figure 8. Floating concrete slab on neoprene strips with mineral wool mat in the void under the slab (image source: CDM)."><figcaption class=caption>Figure 8. Floating concrete slab on neoprene strips with mineral wool mat in the void under the slab (image source: CDM).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>3.</span></strong> Floating screed (7 cm) on resilient underlay, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 67 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +8 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-9.svg data-img-alt="Figure 9. Floating screed on thin neoprene underlay (image source: CDM).">Figure 9</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-9.svg alt="Figure 9. Floating screed on thin neoprene underlay (image source: CDM)."><figcaption class=caption>Figure 9. Floating screed on thin neoprene underlay (image source: CDM).</figcaption></figure>
<h4 class=subsectionHead>Ceiling lining</h4>
<p>
   <strong><span class=cmbx-10>0.</span></strong> Concrete slab 20 cm:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 59 dB<br class=newline><strong><span class=cmbx-10>1.</span></strong> Plasterboard false ceiling on steel hangers rigidly connected to the structure, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 62 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +3 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-10.svg data-img-alt="Figure 10. Plasterboard false ceiling on a steel structure rigidly connected to the external structure (image source: Gyproc).">Figure 10</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-10.svg alt="Figure 10. Plasterboard false ceiling on a steel structure rigidly connected to the external structure (image source: Gyproc)."><figcaption class=caption>Figure 10. Plasterboard false ceiling on a steel structure rigidly connected to the external structure (image source: Gyproc).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>2.</span></strong> Gypsum plasterboard false ceiling on resilient hangers with neoprene, and mineral wool mat in the false ceiling, combined performance:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Rw</mi>
   </math>
   = 76 dB leading to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>ΔRw</mi>
   </math>
   = +17 dB (see <a href=# data-img-src=${config.ASSETS_URL}/05/28/theory/figure-11.svg data-img-alt="Figure 11. Plasterboard false ceiling on anti-vibration hangers to decouple the false ceiling from the external structure (image source: AK Akustik).">Figure 11</a>)
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-11.svg alt="Figure 11. Plasterboard false ceiling on anti-vibration hangers to decouple the false ceiling from the external structure (image source: AK Akustik)."><figcaption class=caption>Figure 11. Plasterboard false ceiling on anti-vibration hangers to decouple the false ceiling from the external structure (image source: AK Akustik).</figcaption></figure>
<h3 class=sectionHead>Calculation example</h3>
<p>
   In this example, we look at the calculation to obtain the sound reduction index of a path that first passes through the floor (flanking) and then radiates through the adjoining wall (direct). This is therefore an ”
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Fd</mi>
   </math>
   ” path.<br class=newline>The situation is as follows: i = F = concrete floor; j = d = party wall.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-12.svg alt="Figure 12. Detail of the elements used to determine the sound attenuation index of the path <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Fd</mi></math> passing through the floor and then radiated via the partition wall."><figcaption class=caption>Figure 12. Detail of the elements used to determine the sound attenuation index of the path <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Fd</mi></math> passing through the floor and then radiated via the partition wall.</figcaption></figure>
<p>
   In this example, it is assumed that the party wall is built directly onto the floor slab. This situation corresponds to a rigid cross joint and the surface mass of element 1 (floor slab) and element 2 (party wall) are almost identical. Thus
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Kij</mi>
   </math>
   is 10 dB. With no lining in this example,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Δ</mi>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is 0 dB. Using the formula given above,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   = 65 dB.<br class=newline>
<p>
   By carrying out the same exercise for all paths i-j, the apparent wall loss can be calculated, as shown in the picture below.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/28/theory/figure-13.svg alt="Figure 13. Determination of the apparent sound reduction index in relation to the sound reduction indices of all the individual paths."><figcaption class=caption>Figure 13. Determination of the apparent sound reduction index in relation to the sound reduction indices of all the individual paths.</figcaption></figure>
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Dd</mi>
            </math>
         <dd>Direct - direct path

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Df</mi>
            </math>
         <dd>Direct - flanking path

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Fd</mi>
            </math>
         <dd>Flanking - direct path

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Ff</mi>
            </math>
         <dd>Flanking - flanking path

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Kij</mi>
            </math>
         <dd>Vibration loss index for the transmission path ij

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <msup>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mi>′</mi>
                  </mrow>
               </msup>
               <mi>w</mi>
            </math>
         <dd>Apparent sound reduction index

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Rw</mi>
            </math>
         <dd>Weighted sound reduction index

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>RDd</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>Weighted sound reduction index for direct transmission.

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>RDf</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>
            Weighted lateral sound reduction index for the transmission path
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Df</mi>
            </math>

         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>RFd</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>
            Weighted lateral sound reduction index for the transmission path
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Fd</mi>
             </math>
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>RFf</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>
            Weighted lateral sound reduction index for the transmission path
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Ff</mi>
            </math>
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Ri</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>Weighted sound reduction index of element i
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Rj</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>Weighted sound reduction index of element j
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>Ss</mi>
            </math>
         <dd>Area of the separative surface
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>ΔRi</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>Improvement of the sound reduction index of element i
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>ΔRj</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>w</mi>
            </math>
         <dd>Improvement of the sound reduction index of element j
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>ΔRw</mi>
            </math>
         <dd>Improvement of the weighted sound reduction index
             
         <dt>
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi mathvariant=italic>lf</mi>
            </math>
         <dd>Current coupling length of the joint between the partition wall and the side walls F and f
             
         <dt>n
         <dd>Number of lateral elements.
    </dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-ISO>[1]
   <dd class=thebibliography id=bib-1>
      <p>International Standard Organization. <span class=cmti-10>ISO 12354-1:2017, Building acoustics </span><span class=cmti-10>— Estimation of acoustic performance of buildings from the performance </span><span class=cmti-10>of elements — Part 1: Airborne sound insulation between rooms</span>. 2017.
</dl>`;

export default theory;
